import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ScoringResult } from '@/lib/api/types';
import { formatNumber } from '@/lib/format';
import { t } from '@/lib/strings';
import { dependencyColour } from '@/theme/tokens';

interface LiveScoreHeaderProps {
  systemName?: string;
  result: ScoringResult | undefined;
  answered: number;
  total: number;
}

/**
 * The dependency index stays visible and updates while the customer answers. It is often what
 * starts the conversation (specification 8.4).
 *
 * The number comes from the PATCH response and is never computed here — the backend is the only
 * calculator (CLAUDE.md, rule 1).
 */
export function LiveScoreHeader({ systemName, result, answered, total }: LiveScoreHeaderProps) {
  const index = result?.dependencyIndex ?? null;
  const band = result?.band ?? null;
  const highestLayer = result?.layers?.reduce(
    (highest, layer) => Math.max(highest, layer.score ?? 0),
    0,
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 3,
        position: 'sticky',
        top: 64,
        zIndex: 2,
        backgroundColor: 'background.paper',
      }}
    >
      {systemName && (
        <Typography
          variant="overline"
          color="text.secondary"
          component="p"
          sx={{ mb: 1, lineHeight: 1.2 }}
        >
          {systemName}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <Box
          aria-hidden
          sx={{
            width: 10,
            height: 40,
            borderRadius: 1,
            backgroundColor: highestLayer ? dependencyColour(highestLayer) : 'divider',
            printColorAdjust: 'exact',
          }}
        />

        <Box sx={{ minWidth: 190 }}>
          <Typography variant="body2" color="text.secondary">
            {t('skema.afhaengighedstalLabel')}
          </Typography>
          <Typography
            component="p"
            aria-live="polite"
            sx={{ fontSize: '1.9rem', fontWeight: 660, lineHeight: 1.15 }}
          >
            {index === null ? '–' : formatNumber(index)}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, minWidth: 200 }}>
          {index === null ? t('skema.afhaengighedstalUfuldstaendig') : band}
        </Typography>

        <Box sx={{ minWidth: 200, flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('skema.fremdrift', { nu: answered, ialt: total })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={total === 0 ? 0 : (answered / total) * 100}
            sx={{ mt: 0.75, height: 8, borderRadius: 999 }}
          />
        </Box>
      </Box>
    </Paper>
  );
}
